import { RefObject, useCallback, useMemo } from 'react'

import cn from 'classnames'
import { useTranslation } from 'react-i18next'

import { SORTING_LABELS, SORTING_OPTIONS } from './constants'
import { ListingsSorting } from './listingsSorting'
import { SortingDropdownProps } from './types'

import { Dropdown, DropdownItem } from '../Dropdown'
import dropdownStyles from '../Dropdown/styles.module.scss'
import { DropdownRenderToggleArgs } from '../Dropdown/types'
import { Icon } from '../Icon'
import { IconStyles } from '../Icon/styles'

export const SortingDropdown = ({
  value,
  onChange,
  className,
  toggleClassName,
  id = 'listingsSorting',
  options,
}: SortingDropdownProps) => {
  const { t } = useTranslation()

  const sortingOptions = options ?? SORTING_OPTIONS

  const getLabel = useCallback(
    (sorting: ListingsSorting) => t(SORTING_LABELS[sorting]),
    [t]
  )

  const currentLabel = useMemo(() => getLabel(value), [getLabel, value])

  const handleSelect = useCallback(
    (sorting: ListingsSorting) => () => onChange(sorting),
    [onChange]
  )

  const isActive = useCallback(
    (sorting: ListingsSorting) => sorting === value,
    [value]
  )

  const renderToggle = useCallback(
    ({ ref, toggle, isOpen }: DropdownRenderToggleArgs) => {
      const handleToggleClick = () => toggle()

      return (
        <button
          type="button"
          ref={ref as RefObject<HTMLButtonElement>}
          onClick={handleToggleClick}
          className={cn(dropdownStyles.toggle, toggleClassName, {
            [dropdownStyles.toggleActive]: true,
            [dropdownStyles.toggleOpen]: isOpen,
          })}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          id={id}
        >
          <span className={dropdownStyles.toggleLabel}>{currentLabel}</span>
          <span className={dropdownStyles.toggleIcon} aria-hidden>
            <Icon path="icons/icon-sorting" style={IconStyles.small} />
          </span>
        </button>
      )
    },
    [currentLabel, id, toggleClassName]
  )

  const renderContent = useCallback(
    () => (
      <>
        {sortingOptions.map((sorting) => (
          <DropdownItem
            key={sorting}
            onSelect={handleSelect(sorting)}
            active={isActive(sorting)}
          >
            {getLabel(sorting)}
          </DropdownItem>
        ))}
      </>
    ),
    [getLabel, handleSelect, isActive, sortingOptions]
  )

  return (
    <div className={className}>
      <Dropdown
        renderToggle={renderToggle}
        renderContent={renderContent}
      />
    </div>
  )
}
